/** Session-only IMEI digits for re-running hub checks from saved reports (same browser tab). */

const STORAGE_KEY = 'redevicer:imei-check-digits-v1'

type CacheEntry = {
  imei: string
  masked: string
  savedAt: number
}

function readList(): CacheEntry[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as CacheEntry[]
    return Array.isArray(parsed) ? parsed.filter((e) => e?.imei && e?.masked) : []
  } catch {
    return []
  }
}

function writeList(list: CacheEntry[]) {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* quota / private mode */
  }
}

export function rememberImeiCheckDigits(imei: string, masked: string): void {
  const digits = imei.replace(/\D/g, '')
  if (digits.length !== 15 || !masked.trim()) return
  const list = readList().filter((e) => !(e.masked === masked && e.imei === digits))
  list.unshift({ imei: digits, masked, savedAt: Date.now() })
  writeList(list.slice(0, 40))
}

function entriesForMasked(masked: string): CacheEntry[] {
  const needle = masked.trim()
  if (!needle) return []
  return readList().filter((entry) => entry.masked === needle)
}

/** Best-effort match for a saved report row (masked IMEI only in API). Session tab only. */
export function resolveImeiForMaskedReport(masked: string, createdAtIso?: string): string | null {
  const matches = entriesForMasked(masked)
  if (!matches.length) return null

  let createdMs = NaN
  if (createdAtIso) {
    try {
      createdMs = new Date(createdAtIso).getTime()
    } catch {
      createdMs = NaN
    }
  }

  if (!Number.isNaN(createdMs)) {
    const byReportTime = [...matches].sort(
      (a, b) => Math.abs(a.savedAt - createdMs) - Math.abs(b.savedAt - createdMs),
    )
    if (byReportTime[0]?.imei) return byReportTime[0].imei
  }

  const newest = [...matches].sort((a, b) => b.savedAt - a.savedAt)
  return newest[0]?.imei ?? null
}
